/**
 * Core-100 R2 — CORE-M: what an institution has said about ONE educational claim.
 *
 * Rendered only when a school has bound its attestation to this claim. "Confirmed by <school>" and
 * the person's own assertion are different facts and look different; a withdrawn attestation is said
 * as withdrawn — the claim stays, as the person's word. Nothing is shown for an unlinked claim:
 * absence of a school link is not evidence the education did not happen.
 */
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';

export type EducationKind = 'technical_school' | 'vocational_programme' | 'college' | 'manufacturer_oem' | 'apprenticeship' | 'other';

export const EDUCATION_KIND_LABEL: Record<EducationKind, string> = {
  technical_school: 'Technical school',
  vocational_programme: 'Vocational programme',
  college: 'College',
  manufacturer_oem: 'Manufacturer / OEM training',
  apprenticeship: 'Apprenticeship',
  other: 'Other',
};

/** Mirror of SQL education_study_status. */
export function educationStudyStatus(start: string | null, end: string | null, graduated: boolean | null, todayIso: string):
  'completed' | 'ended' | 'current' | 'upcoming' | 'unknown' {
  const today = todayIso.slice(0, 10);
  if (graduated === true) return 'completed';
  if (end && end.slice(0, 10) < today) return 'ended';
  if (start && start.slice(0, 10) <= today) return 'current';
  if (start && start.slice(0, 10) > today) return 'upcoming';
  return 'unknown';
}

/**
 * Core-100 R5 (CORE-M Prov / CORE-T D, OD-02/03): an attestation whose school has since been removed from OptiBay
 * stays on the record — it is neither hidden nor invalidated (CORE-T l.44) — and says, factually, that its issuer is
 * gone. A statement of fact, not a judgement of the attestation. Wording recorded as an OD-35/OD-03 copy item.
 */
export const ISSUER_REMOVED_LABEL = 'Issued by a school that has since left OptiBay';

/**
 * CORE-M provenance: by which institution, what kind of attestation, and WHEN (R4: type and date are said when the
 * server returns them; the two-argument form is unchanged).
 */
export function describeAttestation(
  state: string | null | undefined, schoolName: string | null | undefined,
  verificationType?: string | null, at?: string | null,
): string | null {
  const when = at ? ` on ${at.slice(0, 10)}` : '';
  const kind = verificationType ? ` (${verificationType})` : '';
  if (state === 'school_attested') return `Confirmed by ${schoolName ?? 'the school'}${kind}${when}`;
  if (state === 'attestation_withdrawn') return `${schoolName ?? 'The school'} withdrew its confirmation${kind}${when}`;
  return null;
}

export function EducationAttestationBadge({ educationId }: { educationId: string }) {
  const { data } = useQuery({
    queryKey: ['education-attestation', educationId],
    queryFn: async () => {
      const { data: rows, error } = await supabase.rpc('education_attestation_state' as never, { p_education_id: educationId } as never);
      if (error) return null;
      return (((rows ?? []) as unknown) as Array<{
        state: string; school_name: string | null; verification_type: string | null;
        verified_at: string | null; revoked_at: string | null; issuer_removed?: boolean | null;
      }>)[0] ?? null;
    },
  });
  const words = describeAttestation(
    data?.state, data?.school_name, data?.verification_type,
    data?.state === 'attestation_withdrawn' ? data?.revoked_at : data?.verified_at,
  );
  if (!words) return null;
  return (
    <>
      <Badge variant={data?.state === 'school_attested' ? 'default' : 'outline'} className="text-[10px] mt-1" data-testid="education-attestation">
        {words}
      </Badge>
      {data?.issuer_removed === true && (
        <span className="ml-2 text-[10px] text-muted-foreground" data-testid="education-attestation-issuer-removed">
          {ISSUER_REMOVED_LABEL}
        </span>
      )}
    </>
  );
}
